"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog"
import { Button } from "../../ui/button"
import { Calendar } from "../../ui/calendar"
import { AlertTriangle } from 'lucide-react'

interface WeekSelectorProps {
  isOpen: boolean
  onClose: () => void
  currentWeek: string
  onWeekSelect: (week: string) => void
  rejectedRanges?: { timesheetID: number; startDate: string; endDate: string }[]
  selectedDate?: string // Add selected date prop
  maxSelectableISO?: string // End date (ISO yyyy-MM-dd) for current TS; caps calendar
  minSelectableISO?: string // Start date (ISO yyyy-MM-dd) for first timesheet; prevents selection before this date
}

export function WeekSelector({ isOpen, onClose, currentWeek, onWeekSelect, rejectedRanges = [], selectedDate: selectedDateProp, maxSelectableISO, minSelectableISO }: WeekSelectorProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date())
  const [showWarning, setShowWarning] = useState(false)
  const [hasValidSelection, setHasValidSelection] = useState(false)
  const [maxSelectableDate, setMaxSelectableDate] = useState<Date | undefined>(undefined)
  const [minSelectableDate, setMinSelectableDate] = useState<Date | undefined>(undefined)
  const [defaultMonth, setDefaultMonth] = useState<Date | undefined>(undefined)

  useEffect(() => {
    if (!isOpen) return
    // Prefer parent-provided cap; fallback to fetching only if not provided
    if (maxSelectableISO) {
      setMaxSelectableDate(new Date(maxSelectableISO))
    } else {
      const loadCurrentWeek = async () => {
        try {
          const res = await fetch(process.env.REACT_APP_API_URL + `/api/timesheet/v1`, { credentials: "include" })
          if (res.ok) {
            const payload = await res.json()
            const data = payload?.data
            if (data?.endDate) {
              const currentWeekEnd = new Date(data.endDate)
              setMaxSelectableDate(currentWeekEnd)
            }
          }
        } catch {
          // ignore and keep calendar unrestricted
        }
      }
      loadCurrentWeek()
    }
    
    // Set minimum selectable date from prop
    if (minSelectableISO) {
      setMinSelectableDate(new Date(minSelectableISO))
    }
    // Parse currentWeek string to set default month/year in the calendar
    try {
      const [range, yearStr] = currentWeek.split(", ")
      const [startStr] = range.split(" - ")
      const [monAbbr, dayStr] = startStr.split(" ")
      const mm: Record<string, number> = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 }
      const month = mm[monAbbr]
      const year = parseInt(yearStr,10)
      const day = parseInt(dayStr,10)
      setDefaultMonth(new Date(year, month, day))
    } catch {}
    
    // Set selected date from prop if provided
    if (selectedDateProp) {
      try {
        // Parse selectedDateProp like "Monday, August 18"
        const parts = selectedDateProp.split(", ")
        if (parts.length === 2) {
          const monthDay = parts[1].split(" ")
          const monthName = monthDay[0]
          const day = parseInt(monthDay[1])
          const monthMap: Record<string, number> = { January:0, February:1, March:2, April:3, May:4, June:5, July:6, August:7, September:8, October:9, November:10, December:11 }
          const month = monthMap[monthName]
          if (month !== undefined) {
            // Use current year as default
            const year = new Date().getFullYear()
            setSelectedDate(new Date(year, month, day))
          }
        }
      } catch {
        // ignore parsing errors
      }
    }
  }, [isOpen, selectedDateProp])

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date)
      const maxDate = maxSelectableDate || new Date()
      if (date > maxDate) {
        setShowWarning(true)
        setHasValidSelection(false)
      } 
      else {
        setShowWarning(false)
        setHasValidSelection(true)
      }
    }
  }

// Build rejected date ranges for continuous styling (Mon-Fri for each range)
const rejectedRangesList = (rejectedRanges || []).map(r => ({
  from: new Date(r.startDate),
  to: new Date(r.endDate)
}))

// Helper function to check if a date is within any rejected range
const isInRejectedRange = (date: Date): boolean => {
  return rejectedRangesList.some(range => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const rangeFrom = new Date(range.from.getFullYear(), range.from.getMonth(), range.from.getDate())
    const rangeTo = new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate())
    return dateOnly >= rangeFrom && dateOnly <= rangeTo
  })
}

// Helper to check if date is start of a rejected range
const isRejectedRangeStart = (date: Date): boolean => {
  return rejectedRangesList.some(range => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const rangeFrom = new Date(range.from.getFullYear(), range.from.getMonth(), range.from.getDate())
    return dateOnly.getTime() === rangeFrom.getTime()
  })
}

// Helper to check if date is end of a rejected range
const isRejectedRangeEnd = (date: Date): boolean => {
  return rejectedRangesList.some(range => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const rangeTo = new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate())
    return dateOnly.getTime() === rangeTo.getTime()
  })
}

// Helper to check if date is middle of a rejected range
const isRejectedRangeMiddle = (date: Date): boolean => {
  return rejectedRangesList.some(range => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const rangeFrom = new Date(range.from.getFullYear(), range.from.getMonth(), range.from.getDate())
    const rangeTo = new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate())
    return dateOnly > rangeFrom && dateOnly < rangeTo
  })
}

  const handleApply = () => {
    if (selectedDate && !showWarning) {
      // Get the Monday of the selected week
      const selectedDay = selectedDate.getDay()
      const mondayOffset = selectedDay === 0 ? -6 : 1 - selectedDay // Sunday = 0, Monday = 1
      const monday = new Date(selectedDate)
      monday.setDate(selectedDate.getDate() + mondayOffset)
      
      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 6)
      
      const formatDate = (date: Date) => {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
        return `${months[date.getMonth()]} ${date.getDate().toString().padStart(2, '0')}`
      }
      
      const weekString = `${formatDate(monday)} - ${formatDate(sunday)}, ${monday.getFullYear()}`
      onWeekSelect(weekString)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-sm bg-white`}>
        <DialogHeader>
          <DialogTitle className={'text-gray-900'}>Add New Timesheet</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-sm font-medium text-gray-600`}>Week:</span>
              <span className={`text-sm text-gray-600`}>{currentWeek}</span>
            </div>
          </div>

          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            className="rounded-md border w-full"
            captionLayout="dropdown"
            fromYear={2024}
            toYear={new Date().getFullYear() + 1}
            defaultMonth={defaultMonth || selectedDate}
            disabled={(() => {
              const disabled: { before: Date; after?: Date } = {
                before: minSelectableDate || new Date(2024, 9, 1)
              }
              if (maxSelectableDate) {
                disabled.after = maxSelectableDate
              }
              return disabled
            })()}
            modifiers={{
              rejected: (date: Date) => isInRejectedRange(date),
              rejected_start: (date: Date) => isRejectedRangeStart(date),
              rejected_end: (date: Date) => isRejectedRangeEnd(date),
              rejected_middle: (date: Date) => isRejectedRangeMiddle(date)
            }}
            modifiersClassNames={{
              rejected: "bg-red-100 text-red-700 font-semibold border-red-200",
              rejected_start: "bg-red-100 text-red-700 font-semibold border-red-200 rounded-l-full",
              rejected_end: "bg-red-100 text-red-700 font-semibold border-red-200 rounded-r-full",
              rejected_middle: "bg-red-100 text-red-700 font-semibold border-red-200 rounded-none border-y border-l-0 border-r-0"
            }}           
          />

          {showWarning && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Please fill previous timesheets first before filling future timesheets.
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleApply} 
              disabled={showWarning || !hasValidSelection}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              Apply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
